import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUserProfile, linkUserToVendor, listProfilesByVendorRemote, upsertUserProfile } from '../services/profilesService';
import { listVendorsForUser } from '../services/vendorsService';
import { listEvents } from '../services/eventsService';
import { listApplicationsForVendor } from '../services/applicationsService';
import { getVendor } from '../services/vendorsService';
import { getLayoutPublic } from '../services/layoutsService';
import { loadMyReservedBooths } from '../services/reservationsService';
import { sendEmail } from '../services/emailService';
import './Home.css';
import AboutPopup from './AboutPopup';

const Home = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState(null);
  const [userVendors, setUserVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [openEvents, setOpenEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [eventsError, setEventsError] = useState(null);
  const [vendorAppStatusByEvent, setVendorAppStatusByEvent] = useState({});
  const [myReservations, setMyReservations] = useState([]);
  const [reservationsLoading, setReservationsLoading] = useState(false);
  const [vendorTeamProfiles, setVendorTeamProfiles] = useState([]);
  const [vendorTeamLoading, setVendorTeamLoading] = useState(false);
  const [vendorTeamError, setVendorTeamError] = useState(null);
  const [vendorTeamMemberUids, setVendorTeamMemberUids] = useState([]);
  const [vendorTeamApprovingId, setVendorTeamApprovingId] = useState(null);
  const [showClosedEvents, setShowClosedEvents] = useState(false);
  const [layoutsByEvent, setLayoutsByEvent] = useState({}); // Map of eventId -> layout object
  const [showEventMessageModal, setShowEventMessageModal] = useState(false);
  const [eventMessageContent, setEventMessageContent] = useState('');
  const [pendingNavigation, setPendingNavigation] = useState({ event: null, layout: null });

  const sendVendorAccessStatusEmail = async ({ recipientEmail, recipientName, vendorName, status }) => {
    const to = String(recipientEmail || '').trim();
    if (!to) return;
    const displayName = String(recipientName || '').trim() || 'User';
    const actorName = `${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim() || currentUser?.email || 'Vendor admin';
    const vendorLabel = String(vendorName || userVendors?.[0]?.name || userVendors?.[0]?.id || 'Vendor').trim();
    const isApproved = status === 'approved';
    const isRejected = status === 'rejected';
    const subject = isApproved
      ? `Vendor Access Approved: ${vendorLabel}`
      : isRejected
        ? `Vendor Access Request Rejected: ${vendorLabel}`
        : `Vendor Access Removed: ${vendorLabel}`;
    const body = isApproved
      ? `
        <p>Hello ${displayName},</p>
        <p>Your request to access vendor <strong>${vendorLabel}</strong> has been approved.</p>
        <p>Approved by: ${actorName}</p>
        <p>You can now sign in and access vendor data where permitted.</p>
      `
      : isRejected
        ? `
          <p>Hello ${displayName},</p>
          <p>Your request to access vendor <strong>${vendorLabel}</strong> has been rejected.</p>
          <p>Reviewed by: ${actorName}</p>
          <p>If you believe this was a mistake, please contact the vendor administrator.</p>
        `
        : `
          <p>Hello ${displayName},</p>
          <p>Your access to vendor <strong>${vendorLabel}</strong> has been removed.</p>
          <p>Updated by: ${actorName}</p>
          <p>If you believe this was a mistake, please contact the vendor administrator.</p>
        `;
    await sendEmail({ to, subject, type: 'HTML', body });
  };
  
  const isVendorComplete = (v) => {
    if (!v) return false;
    const hasName = !!String(v.name || '').trim();
    const hasType = !!String(v.type || '').trim();
    const hasEmail = !!String(v.email || '').trim();
    const hasPhone = !!String((v.phoneNumber ?? v.phone) || '').trim();
    const hasStreet = !!String(v.addressStreet || '').trim();
    const hasCity = !!String(v.addressCity || '').trim();
    const hasState = !!String(v.addressState || '').trim();
    const hasZip = !!String(v.addressZip || '').trim();
    return hasName && hasType && hasEmail && hasPhone && hasStreet && hasCity && hasState && hasZip;
  };
  const allowRegistration = Array.isArray(userVendors) && userVendors.some(isVendorComplete);
  
  // Load saved layouts from Firestore on component mount
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        // Load user profile for role-based UI
        try {
          const p = await getUserProfile(currentUser.uid);
          setUserProfile(p || null);
        } catch (e) {
          setUserProfile(null);
        }
        // Load vendors associated to this user
        try {
          const vs = await listVendorsForUser(currentUser.uid);
          setUserVendors(vs || []);
        } catch (e) {
          setUserVendors([]);
        }
      } catch (err) {
        if (err.code === 'unavailable' || err.message?.includes('network') || err.message?.includes('Connection failed')) {
          setError('Network connection issue. Trying to reconnect...');
        } else if (err.code === 'permission-denied') {
          setError('Permission denied. Please log out and log back in.');
        } else {
          setError(`Failed to load data: ${err.message || 'Unknown error'}`);
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser]);

  // Load all events (for both registration and vendor dashboard)
  useEffect(() => {
    (async () => {
      try {
        setEventsError(null);
        const all = await listEvents();
        setAllEvents(all || []);
        
        // Be flexible with status values coming from admin (e.g., "Open", "OPEN", "Open for Registration")
        const open = (all || []).filter(ev => {
          const s = (ev?.status || '').toLowerCase();
          // Consider as open if it contains the word 'open' anywhere (e.g., 'open', 'open for registration')
          // Also accept 'active' as a synonym some organizers use
          return s.includes('open') || s === 'active';
        });
        setOpenEvents(open);
      } catch (e) {
        setEventsError('Could not load events.');
        setAllEvents([]);
        setOpenEvents([]);
      }
    })();
  }, []);

  // Load layouts for all events
  useEffect(() => {
    (async () => {
      if (!allEvents.length) return;
      const layoutsMap = {};
      for (const event of allEvents) {
        if (event.layoutId) {
          try {
            const layout = await getLayoutPublic(event.layoutId);
            if (layout) {
              layoutsMap[event.id] = layout;
            }
          } catch (e) {
            // Silently ignore missing layouts (events may reference old/deleted layouts)
            // Only log if it's not a "not found" error
            if (!e.message?.includes('not found')) {
              console.warn(`Could not load layout ${event.layoutId} for event ${event.id}:`, e.message);
            }
          }
        }
      }
      setLayoutsByEvent(layoutsMap);
    })();
  }, [allEvents.length]); // Only re-run when length changes, not on array recreation

  // After vendors and events are loaded, fetch application statuses per vendor per event
  useEffect(() => {
    (async () => {
      if (!currentUser?.uid || !openEvents.length) return;
      const statusMap = {};
      
      // Check applications by vendor ID if user has vendors
      if (userVendors.length > 0) {
        for (const v of userVendors) {
          const apps = await listApplicationsForVendor(v.id, { limitCount: 50 });
          for (const app of apps) {
            if (!app.eventId) continue;
            // Keep the most recently submitted status if multiple
            const existing = statusMap[app.eventId];
            if (!existing || (existing.submittedAt?.seconds || 0) < (app.submittedAt?.seconds || 0)) {
              statusMap[app.eventId] = { status: app.status, submittedAt: app.submittedAt };
            }
          }
        }
      }
      
      // Also check applications by user UID (for Organizers/Admins without vendor records)
      // const userApps = await listApplicationsForUser(currentUser.uid, { limitCount: 50 });
      // for (const app of userApps) {
      //   if (!app.eventId) continue;
      //   // Keep the most recently submitted status if multiple
      //   const existing = statusMap[app.eventId];
      //   if (!existing || (existing.submittedAt?.seconds || 0) < (app.submittedAt?.seconds || 0)) {
      //     statusMap[app.eventId] = { status: app.status, submittedAt: app.submittedAt };
      //   }
      // }
      
      setVendorAppStatusByEvent(statusMap);
    })();
  }, [currentUser?.uid, userVendors.length, openEvents.length]); // Only re-run when lengths change, not on array recreation

  // Load user's reserved booths for the Vendor Dashboard
  useEffect(() => {
    (async () => {
      if (!currentUser?.uid) return;
      
      try {
        setReservationsLoading(true);
        // Get vendorId from userProfile or userVendors
        let vendorId = userProfile?.vendorId;
        if (!vendorId && userVendors && userVendors.length > 0) {
          vendorId = userVendors[0].id;
        }
        if (!vendorId) {
          
          setMyReservations([]);
          setReservationsLoading(false);
          return;
        }
        
        const reservations = await loadMyReservedBooths({ vendorId });
        
        setMyReservations(reservations || []);
      } catch (e) {
        console.error('Failed to load reservations:', e);
        console.error('Error code:', e.code);
        console.error('Error message:', e.message);
        setMyReservations([]);
      } finally {
        setReservationsLoading(false);
      }
    })();
  }, [currentUser?.uid, userProfile?.vendorId, userVendors]);

  // Load Vendor Team (profiles) for the primary vendor the user belongs to
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!currentUser?.uid || !userVendors || userVendors.length === 0) {
        setVendorTeamProfiles([]);
        setVendorTeamMemberUids([]);
        return;
      }
      const vendor = userVendors[0];
      if (!vendor || !vendor.id) {
        setVendorTeamProfiles([]);
        setVendorTeamMemberUids([]);
        return;
      }
      // Only the primary vendor's primary admin may view/manage the Vendor Team here
      if (!vendor.vendorAdmin || String(vendor.vendorAdmin) !== String(currentUser.uid)) {
        setVendorTeamProfiles([]);
        setVendorTeamMemberUids([]);
        return;
      }

      try {
        setVendorTeamLoading(true);
        setVendorTeamError(null);

        // Load protected vendor doc to get memberUids (may fail if not allowed)
        let v = null;
        try {
          v = await getVendor(vendor.id);
        } catch (vendErr) {
          console.warn('Failed to load protected vendor doc for team list:', vendErr?.message || vendErr);
        }
        const members = Array.isArray(v?.memberUids) ? v.memberUids.slice() : [];
        if (!mounted) return;
        setVendorTeamMemberUids(members);

        // Load vendor team profiles via centralized remote helper + fallback
        try {
          const { profiles: list, loadError } = await listProfilesByVendorRemote(vendor.id);
          if (!mounted) return;
          setVendorTeamProfiles(list || []);
          if (loadError) setVendorTeamError(loadError);
        } catch (err) {
          console.error('Failed to load vendor team profiles:', err);
          if (!mounted) return;
          setVendorTeamProfiles([]);
          setVendorTeamError(err?.message || 'Failed to load vendor team profiles');
        }
      } finally {
        if (mounted) setVendorTeamLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [currentUser?.uid, userVendors]);

  const userInfo = {
    type: 'Email Account',
    displayName: currentUser?.displayName || 'User',
    email: currentUser?.email || 'Not provided',
    icon: '✉️'
  };
  const handleLogout = async () => {
    try {
      if (typeof logout === 'function') {
        await logout();
      }
      navigate('/login');
    } catch (err) {
      console.error('Logout failed', err);
      // still navigate to login as a fallback
      try { navigate('/login'); } catch (e) {}
    }
  };
  const isAdmin = (userProfile?.userType === 'Admin');
  const isEventAdmin = (userProfile?.userType === 'EventAdmin');
  const isSystemAdmin = (userProfile?.userType === 'SystemAdmin');
  const dashboardRoleLabel = String(userProfile?.userType || '').trim() || 'Admin';
  const canViewAdminDashboard = ['Admin', 'SystemAdmin', 'Organizer', 'EventAdmin'].includes(String(userProfile?.userType || '').trim());
  const [showAbout, setShowAbout] = useState(false);

  const formatCurrency = (amountInCents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format((typeof amountInCents === 'number' ? amountInCents : 0) / 100);
  };

  const handleBuy = async (event) => {
    try {
      const layout = await getLayoutPublic(event.layoutId);
      if (!layout) {
        alert('Layout not found for this event.');
        return;
      }
      if ((layout.status || '').toLowerCase() !== 'published') {
        alert('Registration is not available yet. Please check back when the layout is published.');
        return;
      }
      if (event?.displayUserMessage) {
        setEventMessageContent(event?.userMessage || '');
        setPendingNavigation({ event, layout });
        setShowEventMessageModal(true);
        return;
      }

      navigate('/booth-selection', { state: { existingLayout: layout, event: event, backTo: '/' } });
    } catch (e) {
      console.error('Failed to open booth selection:', e);
      alert('Unable to open booth selection for this event. Please try again later.');
    }
  };

  const formatPhone = (countryCode, digits) => {
    const d = digits || '';
    if (!d) return '';
    const c = (countryCode ?? '') || '';
    return c ? `${c} ${d}` : d;
  };

  // Removed layout actions from Home

  const firstName = (userProfile?.firstName?.trim()) || (currentUser?.displayName?.trim()?.split(/\s+/)[0]) || 'Friend';
  const displayUserName = ((userProfile?.firstName || '') + ' ' + (userProfile?.lastName || '')).trim() || currentUser?.displayName || 'User';
  const userPhoneDigits = userProfile?.phoneNumber ?? userProfile?.phone ?? currentUser?.phoneNumber ?? '';
  const userAreaCode = userProfile?.userAreaCode ?? userProfile?.countryCode ?? '';
  const userPhone = formatPhone(userAreaCode, userPhoneDigits);
  const userEmail = userProfile?.email || currentUser?.email || '';
  const memberSince = currentUser?.metadata?.creationTime ? new Date(currentUser.metadata.creationTime).toLocaleDateString() : '';
  const lastLogin = currentUser?.metadata?.lastSignInTime ? new Date(currentUser.metadata.lastSignInTime).toLocaleString() : '';

  return (
    <div className="home-container">
      <div className="home-header">
        <h1>Assalamo Alaikum, {firstName}.</h1>
          <div className="admin-actions">
          <button onClick={() => navigate('/profile')} className="new-layout-btn" title="View/Edit your profile">
            👤 Profile
          </button>
          {!isOnline && (
            <div className="connection-status offline">
              📡 Offline - Check your connection
            </div>
          )}
          <button onClick={handleLogout} className="logout-btn" title="Logout">
            🚪 Logout
          </button>
        </div>
      </div>
          {/* Removed generic Browse Vendors / Apply buttons per requirement */}
      <div className="user-card">
        <div className="user-details" style={{ width: '100%' }}>
          <h2 style={{ marginBottom: 12 }}>My User Info</h2>
          {/* Row 1: User Info */}
          <div className="summary-grid">
            <div className="summary-item">
              <div className="summary-label">User Name</div>
              <div className="summary-value"><span className="summary-strong">{displayUserName}</span></div>
            </div>
            <div className="summary-item">
              <div className="summary-label">User Email</div>
              <div className="summary-value"><span className="summary-strong">{userEmail || <span style={{color:'#888'}}>-</span>}</span></div>
            </div>
            <div className="summary-item">
              <div className="summary-label">User Phone</div>
              <div className="summary-value"><span className="summary-strong">{userPhone || <span style={{color:'#888'}}>-</span>}</span></div>
            </div>
          </div>
          {/* Row 2+: One or more Businesses associated with this user */}
          {(userVendors && userVendors.length > 0) ? (
            userVendors.map(v => (
              <div key={v.id} className="summary-grid">
                <div className="summary-item">
                  <div className="summary-label">Business Name</div>
                  <div className="summary-value"><span className="summary-strong">{v.name || <span style={{color:'#888'}}>-</span>}</span></div>
                </div>
                <div className="summary-item">
                  <div className="summary-label">Business Email</div>
                  <div className="summary-value"><span className="summary-strong">{v.email || <span style={{color:'#888'}}>-</span>}</span></div>
                </div>
                <div className="summary-item">
                  <div className="summary-label">Business Phone</div>
                  <div className="summary-value">
                    <span className="summary-strong">
                      {formatPhone(v.businessAreaCode, (v.phoneNumber ?? v.phone)) || <span style={{color:'#888'}}>-</span>}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="summary-grid">
              <div className="summary-item">
                <div className="summary-label">Business Name</div>
                <div className="summary-value"><span className="summary-strong">{userProfile?.businessName || <span style={{color:'#888'}}>-</span>}</span></div>
              </div>
              <div className="summary-item">
                <div className="summary-label">Business Email</div>
                <div className="summary-value"><span className="summary-strong">{userProfile?.businessEmail || <span style={{color:'#888'}}>-</span>}</span></div>
              </div>
              <div className="summary-item">
                <div className="summary-label">Business Phone</div>
                <div className="summary-value">
                  <span className="summary-strong">
                    {formatPhone(userProfile?.businessAreaCode, (userProfile?.businessPhoneNumber ?? userProfile?.businessPhone)) || <span style={{color:'#888'}}>-</span>}
                  </span>
                </div>
              </div>
            </div>
          )}
          <div className="summary-footer">
            <div className="info-item"><strong>Member Since:</strong> {memberSince || '-'}</div>
            <div className="info-item"><strong>Last Login:</strong> {lastLogin || '-'}</div>
          </div>
          <div className="summary-note">
            Please review and keep this contact information up to date to ensure you receive important updates.
          </div>
        </div>
      </div>
      
        {/* Modal: Event User Message (shown before navigating to booth selection) */}
        {showEventMessageModal && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}>
            <div style={{ width: 'min(800px, 96%)', background: '#fff', padding: 20, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}>
              <h3 style={{ marginTop: 0 }}>Message from Organizer</h3>
              <div style={{ marginBottom: 16, color: '#111', whiteSpace: 'pre-wrap' }}>{eventMessageContent || 'No message provided.'}</div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button className="new-layout-btn" onClick={() => { setShowEventMessageModal(false); setPendingNavigation({ event: null, layout: null }); }}>Cancel</button>
                <button className="new-layout-btn" onClick={() => {
                  setShowEventMessageModal(false);
                  const { event, layout } = pendingNavigation || {};
                  setPendingNavigation({ event: null, layout: null });
                  if (layout && event) {
                    navigate('/booth-selection', { state: { existingLayout: layout, event, backTo: '/' } });
                  }
                }}>Proceed to Registration</button>
              </div>
            </div>
          </div>
        )}

      {/* Admin Dashboard section (Admin/Organizer only) */}
      {canViewAdminDashboard && (
        <div className="user-card">
          <h2 className="profile-title" style={{ marginTop: 0 }}>{dashboardRoleLabel} Dashboard</h2>
            <div className="admin-actions">
              <div className="admin-rows">
                <div className="admin-row admin-row--first">
                  {(isAdmin || isSystemAdmin || isEventAdmin) && (
                    <>
                      <button onClick={() => navigate('/admin/layouts')} className="new-layout-btn" title="Layouts administration">Layouts</button>
                      <button onClick={() => navigate('/events')} className="new-layout-btn" title="Events">Events</button>
                      <button onClick={() => navigate('/admin/holds')} className="new-layout-btn" title="Manage Holds for any event">Holds</button>
                    </>
                  )}
                  {(isAdmin || isEventAdmin || userProfile?.userType === 'Organizer') && (
                    <button onClick={() => navigate('/admin/reservation-logs')} className="new-layout-btn" title="View reservation and cart logs">Logs</button>
                  )}
                  <button onClick={() => setShowAbout(true)} className="new-layout-btn" title="About this app">About</button>
                </div>

                <div className="admin-row admin-row--second">
                  <button onClick={() => navigate('/admin/applications')} className="new-layout-btn" title="Review booth applications">Applications</button>
                  <button onClick={() => navigate('/admin/vendors')} className="new-layout-btn" title="Vendors administration">Vendors</button>
                  <button onClick={() => navigate('/transactions')} className="new-layout-btn" title="View payment transactions">Transactions</button>
                  {(isAdmin || isEventAdmin || userProfile?.userType === 'Organizer') && (
                    <>
                      <button onClick={() => navigate('/admin/booths')} className="new-layout-btn" title="Booths status overview">Booth Admin</button>
                      <button onClick={() => navigate('/admin/bazaar-checkin')} className="new-layout-btn" title="Bazaar check-in process">Bazaar Checkin</button>
                    </>
                  )}
                </div>

                <div className="admin-row admin-row--third">
                  {(isAdmin || isSystemAdmin || isEventAdmin) && <button onClick={() => navigate('/test/stripe-payment')} className="new-layout-btn" title="Manual payment page">Manual Payment</button>}
                  {(isAdmin || isSystemAdmin || isEventAdmin) && <button onClick={() => navigate('/admin/manual-reservation')} className="new-layout-btn" title="Create manual reservation">Manual Reservation</button>}
                  {(isAdmin || isSystemAdmin || isEventAdmin) && <button onClick={() => navigate('/admin/email-templates')} className="new-layout-btn" title="Create and manage HTML email templates">Email Templates</button>}
                </div>
              </div>
            </div>
        </div>
      )}

      {/* Vendor Dashboard - Show reserved booths grouped by event status */}

      <AboutPopup show={showAbout} onClose={() => setShowAbout(false)} />
      <div className="user-card">
        <div className="dashboard-row-header">
          <div className="dashboard-row-left">
            <h2 className="profile-title" style={{ margin: 0 }}>
              {userVendors.length > 0 ? `${userVendors[0].name} Dashboard` : (userProfile?.businessName ? `${userProfile.businessName} Dashboard` : 'Vendor Dashboard')}
            </h2>
          </div>
          <div className="dashboard-row-right">
            <button 
              onClick={() => navigate('/transactions')} 
              className="new-layout-btn" 
              title="View your payment history"
              style={{ margin: 0 }}
            >
              💳 My Payments
            </button>
            <button 
              onClick={() => navigate('/attendees')} 
              className="new-layout-btn" 
              title="Manage your event attendees"
              style={{ margin: 0 }}
            >
              👥 My Attendees
            </button>
            <button 
              onClick={() => navigate('/pre-checkin-steps')} 
              className="new-layout-btn" 
              title="Complete pre check-in steps"
              style={{ margin: 0 }}
            >
              ✅ My Consent
            </button>
          </div>
        </div>
        
        {/* Business completion check for registration */}
        {!allowRegistration && (userProfile?.userType !== 'Admin' && userProfile?.userType !== 'Organizer') && (
          <div className="profile-hint" style={{ color: '#b07100', marginBottom: 16, padding: 12, backgroundColor: '#fff8e1', borderRadius: 4 }}>
            To register for events, please complete your Business information first: Name, Type, Email, Phone, and Address.
            <div style={{ marginTop: 10 }}>
              <button onClick={() => navigate('/profile')} className="new-layout-btn" title="Complete your business profile">Go to Profile</button>
            </div>
          </div>
        )}

        {/* Events and Reserved booths section */}
        {(allowRegistration || userProfile?.userType === 'Admin' || userProfile?.userType === 'Organizer') && (
          <>
            {reservationsLoading ? (
              <div className="profile-hint">Loading your reservations...</div>
            ) : (
              <>
              {myReservations.length > 0 && (
                <div className="profile-hint" style={{ color: '#059669', marginBottom: 12 }}>
                  You have {myReservations.length} booth{myReservations.length !== 1 ? 's' : ''} reserved across{' '}
                  {new Set(myReservations.map(r => r.eventId)).size} event(s).
                </div>
              )}
              {/* Group reservations by eventId and separate by event status */}
              {(() => {
                const eventGroups = {};
                myReservations.forEach(res => {
                  if (!eventGroups[res.eventId]) {
                    eventGroups[res.eventId] = [];
                  }
                  eventGroups[res.eventId].push(res);
                });

                // Separate into open and closed events
                const openEventIds = new Set(openEvents.map(e => e.id));
                
                // Create groups for ALL open events (not just those with reservations)
                const openEventGroups = {};
                const closedEventGroups = {};
                
                // First, add all open events (even without reservations)
                openEvents.forEach(event => {
                  openEventGroups[event.id] = eventGroups[event.id] || [];
                });
                
                // Then add closed events that have reservations
                Object.entries(eventGroups).forEach(([eventId, reservations]) => {
                  if (!openEventIds.has(eventId)) {
                    closedEventGroups[eventId] = reservations;
                  }
                });

                // Helper function to render event booth tables
                const renderEventBoothTable = (eventId, reservations) => {
                  const event = allEvents.find(e => e.id === eventId);
                  const eventDisplayName = event?.name || eventId;
                  const eventStatus = event?.status || 'Unknown';
                  const policyLink = String(event?.policyLink || '').trim();
                  const layout = layoutsByEvent[eventId];
                  const layoutDisplayName = layout?.name || layout?.title || '';
                  const appInfo = vendorAppStatusByEvent[eventId];
                  const approved = appInfo?.status === 'Approved';
                  const hasAnyApp = !!appInfo;
                  const showApplyButton = !approved && openEventIds.has(eventId) && (event.allowApplications ?? true);
                  const showBuyButton = approved && event?.layoutId && openEventIds.has(eventId) && (event.allowRegistration ?? true);
                  
                  // Sort reservations by reserved date (most recent first)
                  const sortedReservations = [...reservations].sort((a, b) => {
                    const dateA = a.updatedAt || a.createdAt;
                    const dateB = b.updatedAt || b.createdAt;
                    
                    if (!dateA) return 1;
                    if (!dateB) return -1;
                    
                    try {
                      const tsA = typeof dateA.toMillis === 'function' 
                        ? dateA.toMillis() 
                        : (dateA.seconds ? dateA.seconds * 1000 : new Date(dateA).getTime());
                      const tsB = typeof dateB.toMillis === 'function' 
                        ? dateB.toMillis() 
                        : (dateB.seconds ? dateB.seconds * 1000 : new Date(dateB).getTime());
                      return tsB - tsA; // Descending order (newest first)
                    } catch (e) {
                      return 0;
                    }
                  });
                  
                  // Calculate total for this event
                  const eventTotal = reservations.reduce((sum, res) => {
                    const price = res.price || res.boothDetails?.price || 0;
                    return sum + price;
                  }, 0);

                  return (
                    <div key={eventId} style={{ marginBottom: 24 }}>
                      <div className="event-row-header">
                            <div className="event-header-top">
                              <h3 className="event-header-title" style={{ 
                                fontSize: '20px', 
                                fontWeight: 700, 
                                margin: 0,
                                color: '#1976d2'
                              }}>
                                {eventDisplayName}
                              </h3>
                              {layoutDisplayName && (
                                <span className="event-header-layout" style={{ 
                                  fontSize: '16px',
                                  fontWeight: 600,
                                  color: '#1976d2',
                                  marginLeft: '8px'
                                }}>
                                  - {layoutDisplayName}
                                </span>
                              )}
                              {(event?.startDate || event?.endDate) && (
                                <span className="event-header-dates" style={{ 
                                  fontSize: '14px',
                                  fontWeight: 500,
                                  color: '#6b7280',
                                  marginLeft: '8px'
                                }}>
                                  Event Dates: {[event.startDate, event.endDate].filter(Boolean).join(' - ')}
                                </span>
                              )}
                              {openEventIds.has(eventId) && policyLink && (
                                <a
                                  href={policyLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#dc2626',
                                    marginLeft: '10px',
                                    textDecoration: 'underline'
                                  }}
                                >
                                  Event Policies & FAQs
                                </a>
                              )}
                            </div>

                            <div className="event-header-bottom">
                              <div className="event-actions" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                {showApplyButton && (
                                  <>
                                    <button
                                      className="new-layout-btn"
                                      onClick={async () => {
                                        try {
                                          if (event.layoutId) {
                                            const layout = await getLayoutPublic(event.layoutId);
                                            if (!layout) { alert('Layout not found for this event.'); return; }
                                            if ((layout.status || '').toLowerCase() !== 'published') {
                                              alert('Application is not available yet. Please check back when the layout is published.');
                                              return;
                                            }
                                            navigate('/apply', { state: { event: event, layout } });
                                            return;
                                          }
                                          navigate('/apply', { state: { event: event } });
                                        } catch (e) {
                                          console.error('Failed to open application page:', e);
                                          alert('Unable to open application page for this event. Please try again later.');
                                        }
                                      }}
                                    >
                                      {(hasAnyApp && appInfo?.status === 'Pending') ? 'Update Application' : 'Apply'}
                                    </button>
                                    {hasAnyApp ? (
                                      <span style={{ 
                                        fontSize: '13px',
                                        color: '#b07100',
                                        marginLeft: '12px',
                                        fontStyle: 'italic'
                                      }}>
                                        Application Status: {appInfo.status}
                                      </span>
                                    ) : (
                                      <span style={{ 
                                        fontSize: '13px',
                                        color: '#b07100',
                                        marginLeft: '12px',
                                        fontStyle: 'italic'
                                      }}>
                                        You must submit an application before purchasing a booth.
                                      </span>
                                    )}
                                  </>
                                )}

                                {approved && (
                                  <span style={{ 
                                    fontSize: '13px',
                                    color: '#059669',
                                    fontWeight: 600
                                  }}>
                                    Application Status: Approved
                                  </span>
                                )}

                                {approved && event?.layoutId && openEventIds.has(eventId) && (event.allowRegistration ?? true) && (
                                  <button
                                    className="new-layout-btn"
                                    onClick={() => handleBuy(event)}
                                  >
                                    {reservations.length > 0 ? 'Buy More Booths' : 'Buy Booths'}
                                  </button>
                                )}

                                {approved && event?.layoutId && openEventIds.has(eventId) && !(event.allowRegistration ?? true) && (
                                  <span style={{ 
                                    fontSize: '13px',
                                    color: '#6b7280',
                                    fontStyle: 'italic'
                                  }}>
                                    New booth registration not available at this time.
                                  </span>
                                )}

                                {(!showApplyButton && !showBuyButton && !approved) && (
                                  <span style={{ 
                                    fontSize: '13px',
                                    color: '#6b7280',
                                    fontStyle: 'italic'
                                  }}>
                                    This event is not yet open for vendor applications
                                  </span>
                                )}
                              </div>

                              {reservations.length > 0 && (
                                <div className="event-row-right event-total">
                                  Total: {formatCurrency(eventTotal)}
                                </div>
                              )}
                            </div>
                          </div>
                      {reservations.length > 0 && (
                        <div style={{ overflowX: 'auto' }}>
                          <table className="events-table" style={{ width: '100%', tableLayout: 'fixed' }}>
                            <colgroup>
                              <col style={{ width: '15%' }} />
                              <col style={{ width: '20%' }} />
                              <col style={{ width: '15%' }} />
                              <col style={{ width: '20%' }} />
                              <col style={{ width: '30%' }} />
                            </colgroup>
                            <thead>
                              <tr>
                                <th style={{ textAlign: 'left', padding: '8px' }}>Booth #</th>
                                <th style={{ textAlign: 'left', padding: '8px' }}>Level</th>
                                <th style={{ textAlign: 'center', padding: '8px' }}>Type</th>
                                <th style={{ textAlign: 'right', padding: '8px' }}>Price</th>
                                <th style={{ textAlign: 'left', padding: '8px' }}>Reserved Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortedReservations.map(res => {
                                // Use booth details directly from reservation
                                const level = res.level || res.boothDetails?.level || '-';
                                const isCorner = res.corner === 'Y' || res.corner === true || res.boothDetails?.corner === 'Y' || res.boothDetails?.corner === true;
                                const price = res.price || res.boothDetails?.price || 0;
                                const reservedDate = res.updatedAt || res.createdAt;
                                const boothNumber = res.boothNumber || res.boothDetails?.number || res.boothDetails?.boothNumber || res.boothId;
                                let formattedDate = '-';
                                if (reservedDate) {
                                  try {
                                    const ts = typeof reservedDate.toMillis === 'function' 
                                      ? reservedDate.toMillis() 
                                      : (reservedDate.seconds ? reservedDate.seconds * 1000 : new Date(reservedDate).getTime());
                                    const date = new Date(ts);
                                    formattedDate = date.toLocaleString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                      hour: 'numeric',
                                      minute: '2-digit',
                                      hour12: true,
                                      timeZoneName: 'short'
                                    });
                                  } catch (e) {
                                    formattedDate = '-';
                                  }
                                }

                                return (
                                  <tr key={res.id}>
                                    <td style={{ padding: '8px', borderTop: '1px solid #eee', fontWeight: 600 }}>
                                      {boothNumber}
                                    </td>
                                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>
                                      {level}
                                    </td>
                                    <td style={{ padding: '8px', borderTop: '1px solid #eee', textAlign: 'center' }}>
                                      <span style={{
                                        display: 'inline-block',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        backgroundColor: isCorner ? '#fef3c7' : '#e0f2fe',
                                        color: isCorner ? '#d97706' : '#0369a1'
                                      }}>
                                        {isCorner ? 'Corner' : 'Inline'}
                                      </span>
                                    </td>
                                    <td style={{ padding: '8px', borderTop: '1px solid #eee', textAlign: 'right', fontWeight: 600, paddingRight: '16px' }}>
                                      {formatCurrency(price)}
                                    </td>
                                    <td style={{ padding: '8px', borderTop: '1px solid #eee', color: '#666', fontSize: '14px', paddingLeft: '16px' }}>
                                      {formattedDate}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                };

                return (
                  <>
                    {/* Open Events - Always visible */}
                    {Object.keys(openEventGroups).length > 0 && (
                      <div>
                        <h3 style={{ 
                          fontSize: '14px', 
                          fontWeight: 600, 
                          color: '#059669',
                          marginBottom: 16,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Open Events
                        </h3>
                        {Object.entries(openEventGroups).map(([eventId, reservations]) => 
                          renderEventBoothTable(eventId, reservations)
                        )}
                      </div>
                    )}

                    {/* Closed Events - Collapsible */}
                    {Object.keys(closedEventGroups).length > 0 && (
                      <div style={{ marginTop: 24 }}>
                        <div 
                          onClick={() => setShowClosedEvents(!showClosedEvents)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            padding: '12px',
                            backgroundColor: '#f9fafb',
                            borderRadius: '8px',
                            marginBottom: showClosedEvents ? 16 : 0,
                            border: '1px solid #e5e7eb'
                          }}
                        >
                          <span style={{ 
                            marginRight: 8,
                            fontSize: '18px',
                            transition: 'transform 0.2s',
                            transform: showClosedEvents ? 'rotate(90deg)' : 'rotate(0deg)'
                          }}>
                            ▶
                          </span>
                          <h3 style={{ 
                            fontSize: '14px', 
                            fontWeight: 600, 
                            color: '#6b7280',
                            margin: 0,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            flex: 1
                          }}>
                            Closed Events ({Object.keys(closedEventGroups).length})
                          </h3>
                          <span style={{ 
                            fontSize: '12px',
                            color: '#9ca3af',
                            fontWeight: 500
                          }}>
                            {showClosedEvents ? 'Click to hide' : 'Click to expand'}
                          </span>
                        </div>
                        {showClosedEvents && (
                          <div style={{ marginTop: 8 }}>
                            {Object.entries(closedEventGroups).map(([eventId, reservations]) => 
                              renderEventBoothTable(eventId, reservations)
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </>
        )}
      </div>

      {/* Vendor Team section - visible only to primary vendor admin (effect ensures data loaded only for admin) */}
      {userVendors && userVendors.length > 0 && userVendors[0] && userVendors[0].vendorAdmin && String(userVendors[0].vendorAdmin) === String(currentUser?.uid) && (
      <div className="user-card">
        <div className="dashboard-row-header">
          <div className="dashboard-row-left">
            <h2 className="profile-title" style={{ marginTop: 0 }}>Vendor Team</h2>
            <div style={{ marginBottom: 8, color: '#556' }}>
              Manage users associated with {userVendors[0].name || userVendors[0].id}.
            </div>
            {vendorTeamLoading ? (
              <div>Loading vendor team…</div>
            ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {(vendorTeamProfiles || []).length === 0 && <div style={{ color: '#666' }}>{vendorTeamError || 'No users found for this vendor.'}</div>}
              {(vendorTeamProfiles || []).map(u => {
                const isMember = Array.isArray(vendorTeamMemberUids) && vendorTeamMemberUids.includes(u.id);
                const isVendorAdminUser = userVendors && userVendors[0] && String(userVendors[0].vendorAdmin) === String(u.id);
                return (
                <div key={u.id} className="dashboard-row-left">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderRadius: 6, background: isMember ? '#f7fffa' : '#fff8f0', border: '1px solid #eee' }}>
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <div style={{ fontWeight: 700, color: '#0f172a' }}>
                        {(`${u.firstName || ''} ${u.lastName || ''}`).trim() || u.email || u.id}
                        {!isMember && !isVendorAdminUser && <span style={{ marginLeft: 8, background: '#fef3c7', color: '#92400e', borderRadius: 4, fontSize: 11, padding: '1px 6px', fontWeight: 600 }}>Pending</span>}
                        {' '}<span style={{ color: '#6b7280', fontSize: 12 }}>({u.id})</span>
                      </div>
                      <div style={{ fontSize: 13, color: '#374151' }}>{u.email || ''}</div>
                    </div>
                    <div style={{ minWidth: 240, textAlign: 'right' }}>
                      {isVendorAdminUser ? (
                        <div style={{ color: '#0b5b36', fontWeight: 700 }}>Admin</div>
                      ) : isMember ? (
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                          <div style={{ color: '#065f46', fontWeight: 600 }}>Approved User</div>
                          <button
                            type="button"
                            className="apply-btn"
                            disabled={vendorTeamApprovingId === u.id}
                            onClick={async () => {
                              try {
                                setVendorTeamApprovingId(u.id);
                                await linkUserToVendor(userVendors[0].id, u.id, 'unlink');
                                const remaining = (vendorTeamMemberUids || []).filter(id => id !== u.id);
                                setVendorTeamMemberUids(remaining);
                                await sendVendorAccessStatusEmail({
                                  recipientEmail: u?.email,
                                  recipientName: `${u?.firstName || ''} ${u?.lastName || ''}`,
                                  vendorName: userVendors?.[0]?.name || userVendors?.[0]?.id,
                                  status: 'unlinked'
                                });
                                setVendorTeamApprovingId(null);
                              } catch (err) {
                                console.error('Failed unlinking vendor team member', err);
                                setVendorTeamApprovingId(null);
                              }
                            }}
                          >
                            {vendorTeamApprovingId === u.id ? 'Removing…' : 'Unlink'}
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                          <button
                            type="button"
                            className="apply-btn"
                            disabled={vendorTeamApprovingId === u.id}
                            onClick={async () => {
                              try {
                                setVendorTeamApprovingId(u.id);
                                await linkUserToVendor(userVendors[0].id, u.id, 'link');
                                const merged = Array.from(new Set([...(vendorTeamMemberUids || []), u.id]));
                                setVendorTeamMemberUids(merged);
                                await sendVendorAccessStatusEmail({
                                  recipientEmail: u?.email,
                                  recipientName: `${u?.firstName || ''} ${u?.lastName || ''}`,
                                  vendorName: userVendors?.[0]?.name || userVendors?.[0]?.id,
                                  status: 'approved'
                                });
                                setVendorTeamApprovingId(null);
                              } catch (err) {
                                console.error('Failed approving vendor team member', err);
                                setVendorTeamApprovingId(null);
                              }
                            }}
                          >
                            {vendorTeamApprovingId === u.id ? 'Approving…' : 'Approve Access'}
                          </button>
                          <button
                            type="button"
                            className="cancel-btn"
                            disabled={vendorTeamApprovingId === u.id}
                            onClick={async () => {
                              if (!window.confirm(`Reject access request from ${u?.firstName || ''} ${u?.lastName || ''}`.trim() + '?')) {
                                return;
                              }
                              try {
                                setVendorTeamApprovingId(u.id);
                                await upsertUserProfile(u.id, { vendorId: '', vendorRole: '', linkApproval: 'n/a' });
                                setVendorTeamProfiles((prev) => (prev || []).filter(p => p.id !== u.id));
                                await sendVendorAccessStatusEmail({
                                  recipientEmail: u?.email,
                                  recipientName: `${u?.firstName || ''} ${u?.lastName || ''}`,
                                  vendorName: userVendors?.[0]?.name || userVendors?.[0]?.id,
                                  status: 'rejected'
                                });
                                setVendorTeamApprovingId(null);
                              } catch (err) {
                                console.error('Failed rejecting vendor team request', err);
                                setVendorTeamApprovingId(null);
                              }
                            }}
                          >
                            {vendorTeamApprovingId === u.id ? 'Rejecting…' : 'Reject Access'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
         </div>  
        </div>
      </div>    
      )}

    </div>
  );
};

export default Home;